// Core type definitions for the application

// User roles — DERIVED from the Prisma schema enum, never transcribed (#324).
// This line was a correct hand-copy until slice 001 widened `UserRole` with
// `system`; the file did not have to be edited to become wrong. It also had no
// importers, so nothing forced the compiler to say so — a stale union with no
// consumers narrows silently on the day someone first uses it, which is the
// hazard rather than the inconvenience. Re-exporting leaves ONE home for the
// fact, so the next widening cannot strand this line again.
export type { UserRole } from '@/generated/prisma/client'

// Artist member roles
export type ArtistRole = 'owner' | 'manager' | 'member'

// Event status
export type EventStatus = 'scheduled' | 'live' | 'ended' | 'cancelled'

// Song request status
export type SongRequestStatus = 'pending' | 'playing' | 'completed' | 'skipped'

// Segment request status (alias for transition — same values as SongRequestStatus)
export type SegmentRequestStatus = SongRequestStatus

// Reaction types — now a string to support extensible reaction sets per artist type.
// The original values were: 'upvote' | 'love' | 'fire' | 'applause' | 'wow' | 'laugh' | 'heart_eyes' | 'smirk' | 'melting'
export type ReactionType = string

// Payment types
export type PaymentType = 'tip' | 'subscription' | 'other'

// Payment status
export type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'refunded'

// Setting entity types
export type SettingEntityType = 'user' | 'artist' | 'event'

// Social platforms
export type SocialPlatform = 
  | 'instagram'
  | 'twitter'
  | 'facebook'
  | 'youtube'
  | 'tiktok'
  | 'spotify'
  | 'soundcloud'
  | 'website'
  | 'other'

// Auth providers
export type AuthProvider = 'google' | 'facebook' | 'twitter' | 'credentials'

// Feedback types (pre-written positive feedback)
// Featured feedback shown initially (most common/broad appeal)
export const FEATURED_FEEDBACK_TYPES = [
  'amazing_performance',
  'love_the_energy',
  'incredible_voice',
  'more_feedback', // Special type to trigger modal
] as const

// All feedback types
export const FEEDBACK_TYPES = [
  // Performance quality
  'amazing_performance',
  'incredible_voice',
  'you_sound_amazing',
  'best_ive_heard',
  'gave_me_chills',
  
  // Energy & vibe
  'love_the_energy',
  'perfect_vibe',
  'such_good_vibes',
  'loving_this_set',
  
  // Song-related
  'great_song_choice',
  'love_that_song',
  'this_is_my_jam',
  'play_it_again',
  
  // Crowd & experience
  'awesome_crowd',
  'best_night_ever',
  'so_glad_im_here',
  'this_is_incredible',
  
  // Appreciation
  'thank_you',
  'youre_the_best',
  'never_stop_playing',
  'made_my_night',
  
  // Requests
  'encore',
  'one_more_song',
  'dont_stop',
  
  // Specific compliments
  'love_your_style',
  'amazing_range',
  'killer_guitar',
  'beautiful_lyrics',
  'you_rock',
  'absolute_legend',
] as const

export type FeedbackType = (typeof FEEDBACK_TYPES)[number]

export const FEEDBACK_LABELS: Record<FeedbackType, string> = {
  // Performance quality
  amazing_performance: 'Amazing performance! 🎸',
  incredible_voice: 'Incredible voice! 🎤',
  you_sound_amazing: 'You sound amazing! ✨',
  best_ive_heard: "Best I've heard! 🏆",
  gave_me_chills: 'Gave me chills! 🥶',
  
  // Energy & vibe
  love_the_energy: 'Love the energy! ⚡',
  perfect_vibe: 'Perfect vibe! 🌟',
  such_good_vibes: 'Such good vibes! 🎶',
  loving_this_set: 'Loving this set! 💜',
  
  // Song-related
  great_song_choice: 'Great song choice! 🎵',
  love_that_song: 'Love that song! 💕',
  this_is_my_jam: 'This is my jam! 🕺',
  play_it_again: 'Play it again! 🔁',
  
  // Crowd & experience
  awesome_crowd: 'Awesome crowd! 👏',
  best_night_ever: 'Best night ever! 🌙',
  so_glad_im_here: "So glad I'm here! 🙌",
  this_is_incredible: 'This is incredible! 🤯',
  
  // Appreciation
  thank_you: 'Thank you! ❤️',
  youre_the_best: "You're the best! 🥇",
  never_stop_playing: 'Never stop playing! 🎹',
  made_my_night: 'Made my night! 🌃',
  
  // Requests
  encore: 'Encore! 🔥',
  one_more_song: 'One more song! ☝️',
  dont_stop: "Don't stop! 🚀",
  
  // Specific compliments
  love_your_style: 'Love your style! 😎',
  amazing_range: 'Amazing range! 📈',
  killer_guitar: 'Killer guitar! 🎸',
  beautiful_lyrics: 'Beautiful lyrics! 📝',
  you_rock: 'You rock! 🤘',
  absolute_legend: 'Absolute legend! 👑',
}

// Reaction emoji mapping
export const REACTION_EMOJIS: Record<ReactionType, string> = {
  upvote: '👍',
  love: '❤️',
  fire: '🔥',
  applause: '👏',
  wow: '🤩',
  laugh: '😂',
  heart_eyes: '😍',
  smirk: '😏',
  melting: '🫠',
}

// iTunes search result type
export interface iTunesSearchResult {
  trackId: number
  trackName: string
  artistName: string
  collectionName: string
  artworkUrl100: string
  previewUrl: string
}

// Socket event types
export interface SocketEvents {
  // Event lifecycle
  'event:join': { eventId: string; visitorId: string }
  'event:leave': { eventId: string; visitorId: string }
  'event:status': { eventId: string; status: EventStatus }
  
  // Song requests
  'request:new': { eventId: string; requestId: string; songTitle: string; songArtist: string }
  'request:upvote': { eventId: string; requestId: string }
  'queue:update': { eventId: string }
  
  // Now playing (songId for backward compat, segmentId for new code)
  'nowplaying:update': { eventId: string; songId: string | null; segmentId?: string | null }
  
  // Tips & reactions
  'tip:received': { eventId: string; amount: number; nonce?: string }
  'reaction:received': { eventId: string; type: ReactionType }
  'feedback:received': { eventId: string; type: FeedbackType; nonce?: string }
  
  // Visitor count
  'visitors:count': { eventId: string; count: number }
}

// API response types
export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  error?: string
}

// Song request creation response types
export interface RequestCreateResponse {
  id: string
  eventId: string
  songId: string
  segmentId?: string // Segment ID (new, for transition)
  visitorId: string
  status: SongRequestStatus
  createdAt: Date
  song: {
    id: string
    title: string
    artist: string
  }
  merged?: boolean // True if request was merged into existing request
  mergeType?: 'upvote' | 'boost' // Type of merge that occurred
  hasTip: boolean // True if request has a tip attached
}

// Pagination
export interface PaginationParams {
  page?: number
  limit?: number
}

export interface PaginatedResponse<T> {
  items: T[]
  total: number
  page: number
  limit: number
  totalPages: number
}

